import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { User, Mail, Globe, Edit, Save, Copy, Check, Share2, ExternalLink, Sparkles, Send, Bell } from "lucide-react";
import { toast } from "sonner";
import StatsPanel from "../components/StatsPanel";
import ArchetypeCard from "../components/ArchetypeCard";

const THRESHOLD_PRESETS = [
  { label: "Aggressive", pct: 5, abs: 1, sub: "Every small move" },
  { label: "Balanced", pct: 10, abs: 3, sub: "Default" },
  { label: "Casual", pct: 20, abs: 5, sub: "Only big swings" },
];

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [isPublic, setIsPublic] = useState(user?.is_public || false);
  const [slug, setSlug] = useState(user?.profile_slug || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [monthlyOptIn, setMonthlyOptIn] = useState(user?.monthly_report_opt_in || false);
  const [alertPct, setAlertPct] = useState(user?.alert_pct_threshold ?? 10);
  const [alertAbs, setAlertAbs] = useState(user?.alert_abs_threshold ?? 3);
  const [sendingPreview, setSendingPreview] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setIsPublic(user.is_public || false);
      setSlug(user.profile_slug || "");
      setBio(user.bio || "");
      setMonthlyOptIn(user.monthly_report_opt_in || false);
      setAlertPct(user.alert_pct_threshold ?? 10);
      setAlertAbs(user.alert_abs_threshold ?? 3);
    }
  }, [user]);

  const handleSave = async () => {
    try {
      await api.put("/profile", {
        name,
        is_public: isPublic,
        profile_slug: slug,
        bio,
        monthly_report_opt_in: monthlyOptIn,
        alert_pct_threshold: Number(alertPct) || 0,
        alert_abs_threshold: Number(alertAbs) || 0,
      });
      toast.success("Profile updated");
      setEditing(false);
      refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const handleSendPreview = async () => {
    setSendingPreview(true);
    try {
      const { data } = await api.post("/admin/digest/send-personal-test");
      if (data.status === "sent") toast.success(`Preview sent to ${data.recipient}`);
      else if (data.status === "skipped") toast.error(`Skipped: ${data.reason}`);
      else toast.error(data.error || "Failed to send preview");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSendingPreview(false);
    }
  };

  const profileUrl = `${window.location.origin}/collector/${slug || user?.user_id}`;

  const copyProfileLink = () => {
    navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    toast.success("Profile link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (!user) return null;

  return (
    <div data-testid="profile-page" className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fadeIn max-w-2xl">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-zinc-100 tracking-tight">Profile</h1>
        <p className="text-zinc-500 mt-1 text-sm">Manage your collector profile</p>
      </div>

      {/* Live archetype */}
      <ArchetypeCard
        userIdOrSlug={user.user_id}
        owner={{ name: user.name, profile_slug: user.profile_slug, profile_photo: user.profile_photo }}
      />

      {/* Gamification — stats + full badge gallery */}
      <StatsPanel showBadgeGallery />

      <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-6 space-y-6">
        {/* Avatar */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden">
            {user.profile_photo ? (
              <img src={user.profile_photo} alt="" className="w-full h-full object-cover" />
            ) : (
              <User className="w-8 h-8 text-zinc-500" />
            )}
          </div>
          <div>
            <p className="text-lg font-heading font-semibold text-zinc-100">{user.name}</p>
            <p className="text-sm text-zinc-500">{user.email}</p>
            {user.profile_slug && <p className="text-xs text-zinc-600">@{user.profile_slug}</p>}
          </div>
        </div>

        <Separator className="bg-zinc-800/50" />

        {/* Edit Form */}
        <div className="space-y-4">
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Display Name</Label>
            {editing ? (
              <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-100 mt-1" />
            ) : (
              <p className="text-zinc-200 mt-1">{user.name}</p>
            )}
          </div>

          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Username (URL slug)</Label>
            {editing ? (
              <div className="mt-1">
                <div className="flex items-center gap-1">
                  <span className="text-zinc-600 text-sm">/collector/</span>
                  <Input data-testid="profile-slug-input" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} placeholder="my-username" className="bg-zinc-950 border-zinc-800 text-zinc-100" />
                </div>
                <p className="text-[10px] text-zinc-600 mt-1">Letters, numbers, and dashes only. Min 3 characters.</p>
              </div>
            ) : (
              <p className="text-zinc-200 mt-1">@{user.profile_slug || <span className="text-zinc-600 italic">not set</span>}</p>
            )}
          </div>

          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Bio</Label>
            {editing ? (
              <Input data-testid="profile-bio-input" value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell collectors about yourself..." className="bg-zinc-950 border-zinc-800 text-zinc-100 mt-1" />
            ) : (
              <p className="text-zinc-400 mt-1 text-sm">{user.bio || <span className="text-zinc-600 italic">No bio yet</span>}</p>
            )}
          </div>

          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email</Label>
            <div className="flex items-center gap-2 mt-1">
              <Mail className="w-4 h-4 text-zinc-500" />
              <p className="text-zinc-200">{user.email}</p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Public Profile</Label>
              <p className="text-zinc-500 text-xs mt-0.5">Allow others to view your collection</p>
            </div>
            <Switch data-testid="public-profile-toggle" checked={isPublic} onCheckedChange={(v) => { setIsPublic(v); setEditing(true); }} />
          </div>

          <div data-testid="monthly-report-section" className="flex items-center justify-between bg-gradient-to-r from-rose-950/20 to-amber-950/10 border border-rose-500/15 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded-lg bg-rose-500/10 text-rose-400 flex-shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <Label className="text-zinc-200 text-sm font-medium">Monthly Collector Report</Label>
                <p className="text-zinc-500 text-xs mt-0.5 leading-relaxed">Get a personal email on the 1st of each month — new additions, value change, leaderboard rank, top gainer.</p>
                {monthlyOptIn && (
                  <button
                    data-testid="send-personal-preview"
                    onClick={handleSendPreview}
                    disabled={sendingPreview}
                    className="text-[11px] text-rose-400 hover:text-rose-300 mt-2 flex items-center gap-1 disabled:opacity-50"
                  >
                    <Send className="w-3 h-3" />{sendingPreview ? "Sending..." : "Send me a preview now"}
                  </button>
                )}
              </div>
            </div>
            <Switch data-testid="monthly-report-toggle" checked={monthlyOptIn} onCheckedChange={(v) => { setMonthlyOptIn(v); setEditing(true); }} />
          </div>

          {/* Smart Price Alert Threshold */}
          <div data-testid="alert-threshold-section" className="bg-gradient-to-r from-sky-950/20 to-emerald-950/10 border border-sky-500/15 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded-lg bg-sky-500/10 text-sky-400 flex-shrink-0">
                <Bell className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <Label className="text-zinc-200 text-sm font-medium">Smart Price Alerts</Label>
                <p className="text-zinc-500 text-xs mt-0.5 leading-relaxed">
                  Adjust when you get notified about price moves on your watched releases. Alerts fire when the change exceeds either threshold.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {THRESHOLD_PRESETS.map((p) => {
                const active = Number(alertPct) === p.pct && Number(alertAbs) === p.abs;
                return (
                  <button
                    key={p.label}
                    type="button"
                    data-testid={`threshold-preset-${p.label.toLowerCase()}`}
                    onClick={() => {
                      setAlertPct(p.pct);
                      setAlertAbs(p.abs);
                      setEditing(true);
                    }}
                    className={`text-[11px] font-mono uppercase tracking-wider rounded-full px-3 py-1 border transition-colors ${
                      active
                        ? "bg-sky-500/20 border-sky-500/50 text-sky-300"
                        : "bg-zinc-900/60 border-zinc-800 text-zinc-400 hover:border-sky-500/30 hover:text-zinc-200"
                    }`}
                  >
                    {p.label}
                    <span className="ml-1 normal-case tracking-normal text-[10px] text-zinc-600">· {p.sub}</span>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-zinc-500 uppercase tracking-wider">Percent threshold</Label>
                <div className="relative mt-1">
                  <Input
                    data-testid="alert-pct-input"
                    type="number"
                    min="0"
                    max="500"
                    step="0.5"
                    value={alertPct}
                    onChange={(e) => { setAlertPct(e.target.value); setEditing(true); }}
                    className="bg-zinc-950 border-zinc-800 text-zinc-100 pr-7"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500 pointer-events-none">%</span>
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500 uppercase tracking-wider">Dollar threshold</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500 pointer-events-none">$</span>
                  <Input
                    data-testid="alert-abs-input"
                    type="number"
                    min="0"
                    max="500"
                    step="0.5"
                    value={alertAbs}
                    onChange={(e) => { setAlertAbs(e.target.value); setEditing(true); }}
                    className="bg-zinc-950 border-zinc-800 text-zinc-100 pl-6"
                  />
                </div>
              </div>
            </div>
            <p data-testid="alert-threshold-preview" className="text-[11px] text-zinc-500">
              <span className="text-sky-400">Preview:</span> a watched release goes from $50 →{" "}
              {(() => {
                const pct = Number(alertPct) || 0;
                const abs = Number(alertAbs) || 0;
                const newVal = Math.max(50 + abs, 50 * (1 + pct / 100));
                return `$${newVal.toFixed(0)}`;
              })()}{" "}
              would trigger an alert. <span className="text-zinc-600">Set to 0 to use the default (10% / $3).</span>
            </p>
          </div>

          {/* Share Section */}
          {isPublic && (
            <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-800/50 space-y-3">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-zinc-300 font-medium">Your public profile is live!</span>
              </div>
              <div className="flex items-center gap-2 bg-zinc-950 rounded-lg px-3 py-2 border border-zinc-800">
                <span className="text-xs text-zinc-400 truncate flex-1 font-mono">{profileUrl}</span>
                <button data-testid="copy-profile-link" onClick={copyProfileLink} className="text-xs text-rose-500 hover:text-rose-400 flex items-center gap-1 flex-shrink-0">
                  {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={copyProfileLink} className="bg-rose-600 hover:bg-rose-500 text-white text-xs">
                  <Share2 className="w-3 h-3 mr-1" /> Share Collection
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(profileUrl, "_blank")} className="bg-zinc-900 border-zinc-800 text-zinc-400 text-xs">
                  <ExternalLink className="w-3 h-3 mr-1" /> Preview
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          {editing ? (
            <>
              <Button data-testid="save-profile" onClick={handleSave} className="bg-rose-600 hover:bg-rose-500 text-white">
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
              <Button data-testid="cancel-profile" variant="outline" onClick={() => {
                setEditing(false);
                setName(user.name);
                setIsPublic(user.is_public);
                setSlug(user.profile_slug || "");
                setBio(user.bio || "");
                setMonthlyOptIn(user.monthly_report_opt_in || false);
                setAlertPct(user.alert_pct_threshold ?? 10);
                setAlertAbs(user.alert_abs_threshold ?? 3);
              }} className="bg-zinc-900 border-zinc-800 text-zinc-400">
                Cancel
              </Button>
            </>
          ) : (
            <Button data-testid="edit-profile" onClick={() => setEditing(true)} variant="outline" className="bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800">
              <Edit className="w-4 h-4 mr-1" /> Edit Profile
            </Button>
          )}
        </div>
      </div>

      {/* Account Info */}
      <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-6 space-y-3">
        <h3 className="font-heading text-lg font-semibold text-zinc-100">Account</h3>
        <Separator className="bg-zinc-800/50" />
        <div className="flex justify-between py-1">
          <span className="text-sm text-zinc-500">Role</span>
          <span className="text-sm text-zinc-200 capitalize">{user.role}</span>
        </div>
        <div className="flex justify-between py-1">
          <span className="text-sm text-zinc-500">Member Since</span>
          <span className="text-sm text-zinc-200">{new Date(user.created_at).toLocaleDateString()}</span>
        </div>
        <div className="flex justify-between py-1">
          <span className="text-sm text-zinc-500">User ID</span>
          <span className="text-xs text-zinc-500 font-mono">{user.user_id}</span>
        </div>
      </div>
    </div>
  );
}
